import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Assessment,
  Download,
  EventNote,
  Inventory2,
  MonitorHeart,
  PlayCircle,
  Refresh,
  Search,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';

export interface ReportFiltersProps {
  onSearchChange?: (search: string) => void;
}

export interface ReportPeriod {
  dateFrom: string;
  dateTo: string;
}

interface ReportPeriodFilterProps {
  period: ReportPeriod;
  onPeriodChange: (period: ReportPeriod) => void;
  onApply: () => void;
  loading?: boolean;
  disabled?: boolean;
  extraActions?: React.ReactNode;
}

interface ReportStateProps {
  title: string;
  message?: string;
  onRetry?: () => void;
}

interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | boolean | null | undefined;
}

const navigationItems = [
  { label: 'Обзор', to: '/reports', icon: <Assessment fontSize="small" /> },
  { label: 'Proof-of-Play', to: '/reports/proof-of-play', icon: <PlayCircle fontSize="small" /> },
  { label: 'Uptime', to: '/reports/devices-uptime', icon: <MonitorHeart fontSize="small" /> },
  { label: 'Контент', to: '/reports/content-playback', icon: <Download fontSize="small" /> },
  { label: 'Активность', to: '/reports/system-activity', icon: <EventNote fontSize="small" /> },
  { label: 'Статистика', to: '/reports/content-statistics', icon: <Inventory2 fontSize="small" /> },
];

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

export const getDefaultReportPeriod = (): ReportPeriod => {
  const dateTo = new Date();
  const dateFrom = new Date(dateTo.getTime() - 30 * 24 * 60 * 60 * 1000);

  return {
    dateFrom: toDateInputValue(dateFrom),
    dateTo: toDateInputValue(dateTo),
  };
};

export const getReportPeriodDays = (period: ReportPeriod) => {
  const from = new Date(`${period.dateFrom}T00:00:00`);
  const to = new Date(`${period.dateTo}T23:59:59`);
  const diff = to.getTime() - from.getTime();

  if (!Number.isFinite(diff) || diff < 0) {
    return 30;
  }

  return Math.max(1, Math.ceil(diff / (24 * 60 * 60 * 1000)));
};

export const toApiPeriodParams = (period: ReportPeriod) => ({
  date_from: `${period.dateFrom}T00:00:00`,
  date_to: `${period.dateTo}T23:59:59`,
});

export const isReportPeriodValid = (period: ReportPeriod) => (
  Boolean(period.dateFrom && period.dateTo) && period.dateFrom <= period.dateTo
);

export const getReportErrorMessage = (error: unknown, fallback = 'Не удалось загрузить отчет') => {
  if (error && typeof error === 'object') {
    const apiError = error as { detail?: unknown; message?: unknown };
    if (typeof apiError.detail === 'string') return apiError.detail;
    if (typeof apiError.message === 'string') return apiError.message;
  }

  return fallback;
};

const escapeCsvCell = (value: string | number | boolean | null | undefined) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n\r;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildCsv = <T,>(rows: T[], columns: CsvColumn<T>[]) => {
  const header = columns.map((column) => escapeCsvCell(column.header)).join(',');
  const body = rows.map((row) => columns.map((column) => escapeCsvCell(column.value(row))).join(','));
  return [header, ...body].join('\n');
};

export const downloadTextFile = (content: string, filename: string, type = 'text/csv;charset=utf-8') => {
  const blob = new Blob([content], { type });
  downloadBlobFile(blob, filename);
};

export const downloadBlobFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ReportFilters: React.FC<ReportFiltersProps> = ({ onSearchChange }) => {
  return (
    <Paper sx={{ p: 2 }}>
      <TextField
        placeholder="Поиск отчетов..."
        size="small"
        sx={{ width: { xs: '100%', sm: 320 } }}
        InputProps={{
          startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
        onChange={(event) => onSearchChange?.(event.target.value)}
      />
    </Paper>
  );
};

export const ReportsNavigation: React.FC = () => {
  const location = useLocation();

  return (
    <Paper sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {navigationItems.map((item) => {
          const isActive = location.pathname === item.to;

          return (
            <Chip
              key={item.to}
              clickable
              component={Link}
              to={item.to}
              icon={item.icon}
              label={item.label}
              color={isActive ? 'primary' : 'default'}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{ textDecoration: 'none' }}
            />
          );
        })}
      </Stack>
    </Paper>
  );
};

export const ReportPeriodFilter: React.FC<ReportPeriodFilterProps> = ({
  period,
  onPeriodChange,
  onApply,
  loading = false,
  disabled = false,
  extraActions,
}) => {
  const valid = isReportPeriodValid(period);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          label="Дата от"
          type="date"
          size="small"
          value={period.dateFrom}
          onChange={(event) => onPeriodChange({ ...period, dateFrom: event.target.value })}
          InputLabelProps={{ shrink: true }}
          error={!valid}
          sx={{ width: { xs: '100%', md: 180 } }}
        />
        <TextField
          label="Дата до"
          type="date"
          size="small"
          value={period.dateTo}
          onChange={(event) => onPeriodChange({ ...period, dateTo: event.target.value })}
          InputLabelProps={{ shrink: true }}
          error={!valid}
          sx={{ width: { xs: '100%', md: 180 } }}
        />
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={onApply}
          disabled={loading || disabled || !valid}
        >
          Обновить
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {extraActions}
      </Stack>
      {!valid && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          Дата начала не может быть позже даты окончания.
        </Typography>
      )}
    </Paper>
  );
};

export const ReportEmptyState: React.FC<ReportStateProps> = ({ title, message }) => (
  <Box sx={{ p: 4, textAlign: 'center' }}>
    <Typography variant="h6">{title}</Typography>
    {message && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {message}
      </Typography>
    )}
  </Box>
);

export const ReportErrorState: React.FC<ReportStateProps> = ({ title, message, onRetry }) => (
  <Alert
    severity="error"
    action={onRetry ? (
      <Button color="inherit" size="small" onClick={onRetry}>
        Повторить
      </Button>
    ) : undefined}
  >
    <Typography variant="subtitle2">{title}</Typography>
    {message && <Typography variant="body2">{message}</Typography>}
  </Alert>
);

export default ReportFilters;
